import { useState } from "react";

import { Field, FieldGrid, FieldSet } from "../../components/Fields";
import { I } from "../../components/icons";
import { NavRow, RowGroup } from "../../components/Rows";
import { useKaya } from "../../store/kayaStore";
import { useProfileForm } from "./profileForm";
import SettingsPage from "./SettingsPage";

/** Whether there is a treadmill at all, and if so what the guided protocol walks at. A machine
 *  program transcribed under Programs carries its own speeds and ignores these. */
export default function TreadmillSettings() {
  const { f, set, save, dirty } = useProfileForm();
  const { S, update, answerSetupTreadmill } = useKaya();
  const programCount = S.profile.programs?.length ?? 0;

  // "Walk flat" is not in the shared form, so it is held here and written with the rest on Save —
  // one switch on this page saving instantly while its neighbours wait would be a trap.
  const [flat, setFlat] = useState(!!S.profile.inclineOverride);
  const flatDirty = flat !== !!S.profile.inclineOverride;

  const saveAll = async () => {
    await save();
    // Saving the page that asks the question answers it, even with the switch left as it was.
    answerSetupTreadmill();
    if (flatDirty)
      update((d) => {
        d.profile.inclineOverride = flat;
      });
  };

  return (
    <SettingsPage
      title="Treadmill"
      intro="The guided interval protocol and the speeds it walks at."
      onSave={() => void saveAll()}
      dirty={dirty || flatDirty}
    >
      <RowGroup>
        <NavRow
          kind="toggle"
          title="I have a treadmill"
          subtitle={
            f.hasTreadmill
              ? "Off hides the interval protocol, its speeds and machine programs; free walks stay."
              : "Nothing is deleted — logged treadmill sessions stay, and turning this on brings the protocol back as it was."
          }
          checked={f.hasTreadmill}
          onChange={(v) => set("hasTreadmill", v)}
        />
      </RowGroup>

      {f.hasTreadmill ? (
        <>
          <FieldSet legend="Weeks 1–2">
            <FieldGrid>
              <Field
                label="Brisk"
                unit="km/h"
                type="number"
                step="0.1"
                value={f.brisk1}
                onChange={(v) => set("brisk1", v)}
              />
              <Field
                label="Easy"
                unit="km/h"
                type="number"
                step="0.1"
                value={f.easy1}
                onChange={(v) => set("easy1", v)}
              />
            </FieldGrid>
          </FieldSet>

          <FieldSet legend="Week 3 onwards">
            <FieldGrid>
              <Field
                label="Brisk"
                unit="km/h"
                type="number"
                step="0.1"
                value={f.brisk}
                onChange={(v) => set("brisk", v)}
              />
              <Field
                label="Easy"
                unit="km/h"
                type="number"
                step="0.1"
                value={f.easy}
                onChange={(v) => set("easy", v)}
              />
            </FieldGrid>
          </FieldSet>

          <FieldSet legend="Incline">
            <Field
              label="Incline on brisk blocks"
              unit="%"
              type="number"
              step="1"
              min="0"
              max="6"
              value={f.incline}
              onChange={(v) => set("incline", v)}
              hint="0 if an incline aggravates your hips."
            />
          </FieldSet>

          <RowGroup heading="Programs">
            <NavRow
              kind="toggle"
              title="Walk flat for programs"
              subtitle="Ignore each program's incline — kinder on the hips."
              checked={flat}
              onChange={setFlat}
            />
            <NavRow
              icon={I.treadmill}
              title="Programs"
              subtitle="Machine programs, copied from the console"
              value={programCount ? `${programCount} saved` : "None yet"}
              to="/train/programs"
            />
          </RowGroup>
        </>
      ) : null}
    </SettingsPage>
  );
}
